import clsx from "clsx";
import { useLocale, useTranslations } from "next-intl";
import React, { useEffect, useState } from "react";

import {
	APIProvider,
	AdvancedMarker,
	Map as GoogleMap,
	InfoWindow,
	MapMouseEvent,
	Pin,
	useAdvancedMarkerRef,
} from "@vis.gl/react-google-maps";

import {
	haversineDistance,
	positionToPoint,
	sortByDistanceProperty,
	type Position,
} from "@/lib/util";

import ZorgbaseResult from "@/app/api/filter/[postcode]/ZorgbaseResult";

import { ShowOptions } from "../FormPostcode";
import MapMarker from "./MapMarker";
import OrganisationResult from "./OrganisationResult";

import styles from './Map.module.scss';


function showInResults(item: ZorgbaseResult, accepts: ShowOptions) {
	let showResult = false;
	switch (accepts) {
		case ShowOptions.all:
			showResult = true;
			break;
		case ShowOptions.patients:
			showResult = Boolean(item.acceptsPatients);
			break;
		case ShowOptions.passersby:
			showResult = Boolean(item.acceptsPassersBy);
			break;
	}

	return showResult;
}

// export type MapDisplayType = "All" | "Patients" | "PassersBy";


export interface MapDisplayProps {
	organisations: ZorgbaseResult[];
	location: Position;
	type: ShowOptions;
	displayMap: boolean;
}

const NUMBER_OF_RESULTS_TO_SHOW = 5;

// https://react-google-maps-api-docs.netlify.app/
function Map({
	organisations: defaultOrganisations,
	location: defaultLocation,
	type,
	displayMap
}: MapDisplayProps) {
	const t = useTranslations();
	const locale = useLocale();

	const [markerRef, marker] = useAdvancedMarkerRef();
	const [location, setLocation] = useState(defaultLocation);
	// const [meMarkerInfoWindowShown, meMarkerSetInfoWindowShown] = useState(true);
	const [center, setCenter] = useState<Position | undefined>(defaultLocation);
	const [organisations, setOrganisations] = useState<Array<ZorgbaseResult>>(defaultOrganisations);

	// change meMarker location when a new default is found from Device location
	useEffect(() => {
		setLocation(defaultLocation);
	}, [defaultLocation]);

	// when location is changed by a map click or device detection, set the meMarker to it's position
	useEffect(() => {
		if (location && marker) {
			// console.log(location, marker);
			marker.position = location;
			setCenter(location);
		}
	}, [location, marker]);

	// then the location changes, change the displayed organisations list
	useEffect(() => {
		// console.log('Found location', location);
		const orgs: ZorgbaseResult[] = [];
		// calculate the distance to the new user location
		const position = positionToPoint(location);
		for (let i = 0; i < defaultOrganisations.length; i++) {
			const org = defaultOrganisations[i];

			orgs.push({
				...defaultOrganisations[i],
				distance: location
					? haversineDistance(position!, org)
					: 0,
			});
		}
		// sort the organisations by calculated distance
		setOrganisations(sortByDistanceProperty(orgs) as ZorgbaseResult[]);
	}, [defaultOrganisations, location]);

	const organisationsToDisplay = organisations
		.filter((item) => showInResults(item, type))
		// .sort((a, b) => a.distance! - b.distance!)
		.slice(0, NUMBER_OF_RESULTS_TO_SHOW);

	return (
		<div className="flex-row" style={{ gap: '10px' }}>
			<div className="col-6">
				{organisationsToDisplay.map(
					(item: ZorgbaseResult, _index) => {
						return (
							<OrganisationResult
								key={`or-${item.id}`}
								result={item}
							/>
						);
					},
				)}
			</div>
			<div className={clsx('col-6', !displayMap ? styles.MapContainer : '')}>
				<APIProvider
					apiKey={process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}
					libraries={["marker"]}
					language={locale}
				>
					<GoogleMap
						style={{ height: "440px", width: "100%", overflow: "hidden", borderRadius: "8px" }}
						clickableIcons={false}
						zoom={13}
						controlled={true}
						reuseMaps={true}
						mapId={process.env.NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID}
						fullscreenControl={false}
						mapTypeControl={false}
						streetViewControl={false}
						zoomControl={false}
						tilt={0}
						disableDefaultUI={true}
						gestureHandling={"cooperative"}
						center={center}
					>
						{organisations.map((item, index) => (
							<MapMarker
								organisation={item}
								type={type}
								key={`organisation-marker-${index}`}
							/>
						))}

						<AdvancedMarker
							key="marker-me"
							ref={markerRef}
							position={location}
						>
							<Pin />
						</AdvancedMarker>
					</GoogleMap>
				</APIProvider>
			</div>
		</div>
	);
}

export default Map;
